import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import type {
  AutoUncleVinAndLpnLookupResponse,
  AutoUncleCarInfoLookupResponse,
} from "@/types/autoUncle";

// Mock data for different registration numbers
const MOCK_DATA: Record<
  string,
  AutoUncleVinAndLpnLookupResponse | null
> = {
  AAA111: {
    car: {
      fuel: "Bensin",
      engine_size: "2.0",
      hp: 190,
      year: 2018,
      km: 45000,
      fuel_economy: "12.5",
      airbags: 8,
      green_tax: 1200,
      has_auto_gear: true,
      has_abs: true,
      has_esp: true,
      has_airbags: true,
      has_servo: true,
      has_cent_lock: true,
      brand: "Volkswagen",
      car_model: "Passat Alltrack",
      variant: "2.0 TDI 190 AUT.",
      body: "Kombi",
      reference_id: "volkswagen_passat_alltrack",
      lpn: "aaa111",
      vin: "WVWZZZ1KZJW123456",
    },
    raw_dmr_info: [
      { "Mærke, Model, Variant": "VOLKSWAGEN, PASSAT ALLTRACK, 2.0 TDI 190 AUT." },
      { "Reg.nr": "AAA111" },
      { "Stelnr.": "WVWZZZ1KZJW123456" },
      { "Art": "Personbil" },
      { "1. reg. dato": "2018-05-15" },
      { "Forbrug i KM/L": "12.5" },
      { "Motorstr. i CCM": "1968" },
      { "Brændstof": "Diesel" },
      { "Ydelse i kW": "140" },
    ],
  },
  BBB222: {
    car: {
      fuel: "El",
      engine_size: "0.0",
      hp: 204,
      year: 2021,
      km: 12000,
      fuel_economy: "0.0",
      airbags: 10,
      green_tax: 0,
      has_auto_gear: true,
      has_abs: true,
      has_esp: true,
      has_airbags: true,
      has_servo: true,
      has_cent_lock: true,
      brand: "Tesla",
      car_model: "Model 3",
      variant: "Long Range AWD",
      body: "Sedan",
      reference_id: "tesla_model_3",
      lpn: "bbb222",
      vin: "5YJ3E1EA1MF123456",
    },
    raw_dmr_info: [
      { "Mærke, Model, Variant": "TESLA, MODEL 3, LONG RANGE AWD" },
      { "Reg.nr": "BBB222" },
      { "Stelnr.": "5YJ3E1EA1MF123456" },
      { "Art": "Personbil" },
      { "1. reg. dato": "2021-03-20" },
      { "Forbrug i KM/L": "N/A" },
      { "Motorstr. i CCM": "0" },
      { "Brændstof": "El" },
      { "Ydelse i kW": "204" },
    ],
  },
  CCC333: {
    car: {
      fuel: "Diesel",
      engine_size: "1.6",
      hp: 112,
      year: 2012,
      km: 99000,
      fuel_economy: "20.8",
      airbags: 6,
      green_tax: 1910,
      has_auto_gear: true,
      has_abs: true,
      has_esp: true,
      has_airbags: true,
      has_servo: true,
      has_cent_lock: true,
      brand: "Citroën",
      car_model: "C4 Picasso",
      variant: "HDI 110 AUT. 82KW",
      body: "Minibuss",
      reference_id: "citro_n_c4_picasso",
      lpn: "ccc333",
      vin: "VF7UD9HR8CJ538185",
    },
    raw_dmr_info: [
      { "Mærke, Model, Variant": "CITROËN, C4 PICASSO, HDI 110 AUT." },
      { "Reg.nr": "CCC333" },
      { "Stelnr.": "VF7UD9HR8CJ538185" },
      { "Art": "Personbil" },
      { "1. reg. dato": "2012-02-27" },
      { "Forbrug i KM/L": "20.8" },
      { "Motorstr. i CCM": "1560" },
      { "Brændstof": "Diesel" },
      { "Ydelse i kW": "82" },
    ],
  },
  DDD444: {
    car: {
      fuel: "Bensin",
      engine_size: "1.8",
      hp: 140,
      year: 2019,
      km: 67000,
      fuel_economy: "14.2",
      airbags: 7,
      green_tax: 1050,
      has_auto_gear: false,
      has_abs: true,
      has_esp: true,
      has_airbags: true,
      has_servo: true,
      has_cent_lock: true,
      brand: "Volvo",
      car_model: "V60",
      variant: "T4 MOMENTUM",
      body: "Kombi",
      reference_id: "volvo_v60",
      lpn: "ddd444",
      vin: "YV1BW6820K1123456",
    },
    raw_dmr_info: [
      { "Mærke, Model, Variant": "VOLVO, V60, T4 MOMENTUM" },
      { "Reg.nr": "DDD444" },
      { "Stelnr.": "YV1BW6820K1123456" },
      { "Art": "Personbil" },
      { "1. reg. dato": "2019-08-10" },
      { "Forbrug i KM/L": "14.2" },
      { "Motorstr. i CCM": "1969" },
      { "Brændstof": "Bensin" },
      { "Ydelse i kW": "140" },
    ],
  },
  EEE555: {
    car: {
      fuel: "Hybrid",
      engine_size: "2.5",
      hp: 184,
      year: 2020,
      km: 35000,
      fuel_economy: "5.8",
      airbags: 9,
      green_tax: 500,
      has_auto_gear: true,
      has_abs: true,
      has_esp: true,
      has_airbags: true,
      has_servo: true,
      has_cent_lock: true,
      brand: "Toyota",
      car_model: "RAV4",
      variant: "HYBRID AWD-i",
      body: "SUV",
      reference_id: "toyota_rav4",
      lpn: "eee555",
      vin: "JTMB1RFVXLJ123456",
    },
    raw_dmr_info: [
      { "Mærke, Model, Variant": "TOYOTA, RAV4, HYBRID AWD-i" },
      { "Reg.nr": "EEE555" },
      { "Stelnr.": "JTMB1RFVXLJ123456" },
      { "Art": "Personbil" },
      { "1. reg. dato": "2020-06-12" },
      { "Forbrug i KM/L": "5.8" },
      { "Motorstr. i CCM": "2487" },
      { "Brændstof": "Hybrid" },
      { "Ydelse i kW": "184" },
    ],
  },
  FFF666: {
    car: {
      fuel: "Diesel",
      engine_size: "2.0",
      hp: 150,
      year: 2017,
      km: 125000,
      fuel_economy: "18.5",
      airbags: 6,
      green_tax: 1500,
      has_auto_gear: true,
      has_abs: true,
      has_esp: true,
      has_airbags: true,
      has_servo: true,
      has_cent_lock: true,
      brand: "BMW",
      car_model: "320d",
      variant: "2.0 D AUT.",
      body: "Sedan",
      reference_id: "bmw_320d",
      lpn: "fff666",
      vin: "WBA3B1C52EK123456",
    },
    raw_dmr_info: [
      { "Mærke, Model, Variant": "BMW, 320D, 2.0 D AUT." },
      { "Reg.nr": "FFF666" },
      { "Stelnr.": "WBA3B1C52EK123456" },
      { "Art": "Personbil" },
      { "1. reg. dato": "2017-11-22" },
      { "Forbrug i KM/L": "18.5" },
      { "Motorstr. i CCM": "1995" },
      { "Brændstof": "Diesel" },
      { "Ydelse i kW": "150" },
    ],
  },
  GGG777: {
    car: {
      fuel: "Bensin",
      engine_size: "1.4",
      hp: 122,
      year: 2016,
      km: 88000,
      fuel_economy: "16.0",
      airbags: 6,
      green_tax: 950,
      has_auto_gear: false,
      has_abs: true,
      has_esp: true,
      has_airbags: true,
      has_servo: true,
      has_cent_lock: true,
      brand: "Audi",
      car_model: "A3",
      variant: "1.4 TFSI",
      body: "Sedan",
      reference_id: "audi_a3",
      lpn: "ggg777",
      vin: "WAUZZZ8V9G1123456",
    },
    raw_dmr_info: [
      { "Mærke, Model, Variant": "AUDI, A3, 1.4 TFSI" },
      { "Reg.nr": "GGG777" },
      { "Stelnr.": "WAUZZZ8V9G1123456" },
      { "Art": "Personbil" },
      { "1. reg. dato": "2016-04-05" },
      { "Forbrug i KM/L": "16.0" },
      { "Motorstr. i CCM": "1395" },
      { "Brændstof": "Bensin" },
      { "Ydelse i kW": "122" },
    ],
  },
  HHH888: {
    car: {
      fuel: "Laddhybrid",
      engine_size: "1.5",
      hp: 150,
      year: 2022,
      km: 15000,
      fuel_economy: "4.5",
      airbags: 8,
      green_tax: 300,
      has_auto_gear: true,
      has_abs: true,
      has_esp: true,
      has_airbags: true,
      has_servo: true,
      has_cent_lock: true,
      brand: "Mercedes-Benz",
      car_model: "C220d",
      variant: "EQ POWER 4MATIC",
      body: "Sedan",
      reference_id: "mercedes_c220d",
      lpn: "hhh888",
      vin: "WDD2130071A123456",
    },
    raw_dmr_info: [
      { "Mærke, Model, Variant": "MERCEDES-BENZ, C220D, EQ POWER 4MATIC" },
      { "Reg.nr": "HHH888" },
      { "Stelnr.": "WDD2130071A123456" },
      { "Art": "Personbil" },
      { "1. reg. dato": "2022-01-18" },
      { "Forbrug i KM/L": "4.5" },
      { "Motorstr. i CCM": "1950" },
      { "Brændstof": "Laddhybrid" },
      { "Ydelse i kW": "150" },
    ],
  },
  III999: {
    car: {
      fuel: "Bensin",
      engine_size: "1.6",
      hp: 125,
      year: 2015,
      km: 145000,
      fuel_economy: "15.5",
      airbags: 6,
      green_tax: 1100,
      has_auto_gear: false,
      has_abs: true,
      has_esp: true,
      has_airbags: true,
      has_servo: true,
      has_cent_lock: true,
      brand: "Ford",
      car_model: "Focus",
      variant: "1.6 TI-VCT",
      body: "Halvkombi",
      reference_id: "ford_focus",
      lpn: "iii999",
      vin: "WF0XXXGB5GGA12345",
    },
    raw_dmr_info: [
      { "Mærke, Model, Variant": "FORD, FOCUS, 1.6 TI-VCT" },
      { "Reg.nr": "III999" },
      { "Stelnr.": "WF0XXXGB5GGA12345" },
      { "Art": "Personbil" },
      { "1. reg. dato": "2015-09-30" },
      { "Forbrug i KM/L": "15.5" },
      { "Motorstr. i CCM": "1596" },
      { "Brændstof": "Bensin" },
      { "Ydelse i kW": "125" },
    ],
  },
  JJJ000: {
    car: {
      fuel: "Diesel",
      engine_size: "2.2",
      hp: 200,
      year: 2020,
      km: 42000,
      fuel_economy: "17.0",
      airbags: 8,
      green_tax: 1350,
      has_auto_gear: true,
      has_abs: true,
      has_esp: true,
      has_airbags: true,
      has_servo: true,
      has_cent_lock: true,
      brand: "Land Rover",
      car_model: "Discovery Sport",
      variant: "SD4 HSE",
      body: "SUV",
      reference_id: "land_rover_discovery_sport",
      lpn: "jjj000",
      vin: "SALGA2BE4LA123456",
    },
    raw_dmr_info: [
      { "Mærke, Model, Variant": "LAND ROVER, DISCOVERY SPORT, SD4 HSE" },
      { "Reg.nr": "JJJ000" },
      { "Stelnr.": "SALGA2BE4LA123456" },
      { "Art": "Personbil" },
      { "1. reg. dato": "2020-07-08" },
      { "Forbrug i KM/L": "17.0" },
      { "Motorstr. i CCM": "2179" },
      { "Brændstof": "Diesel" },
      { "Ydelse i kW": "200" },
    ],
  },
};

// Toggle to use mocked data or real API
const USE_MOCK_DATA = true;

/**
 * Vehicle Lookup Service
 * Backend will call AutoUncle: GET /service_api/v1/car_info_lookup?lpn=<lpn>
 */
export class VehicleLookupService {
  /**
   * Get vehicle information by license plate number (LPN) or VIN
   * Backend will call AutoUncle car_info_lookup API
   * GET /admin/vehicle-lookup?lpn=<lpn>&vin=<vin>
   */
  static async getVehicleInformation(
    lpn?: string,
    vin?: string
  ): Promise<AutoUncleCarInfoLookupResponse> {
    if (USE_MOCK_DATA) {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Check if we have mock data for this registration number
      const searchKey = lpn?.toUpperCase().trim();
      if (searchKey && MOCK_DATA[searchKey]) {
        return {
          car: MOCK_DATA[searchKey]!.car,
        };
      }

      // If no mock data found, throw error (backend would return 404)
      throw new Error("Fordonet hittades inte");
    }

    // Backend will call AutoUncle: GET /service_api/v1/car_info_lookup?lpn=<lpn>
    const params: Record<string, string> = {};
    if (lpn) params.lpn = lpn;
    if (vin) params.vin = vin;

    const response = await api.get<{ success: true; data: AutoUncleCarInfoLookupResponse }>(
      `/admin/vehicle-lookup`,
      { params }
    );
    
    return response.data;
  }

  /**
   * Legacy method - kept for backward compatibility
   * @deprecated Use getVehicleInformation instead
   */
  static async carInfoLookup(
    lpn?: string,
    vin?: string
  ): Promise<AutoUncleCarInfoLookupResponse> {
    return this.getVehicleInformation(lpn, vin);
  }
}
